import React, { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import  { ChapterSection } from "../ServicesMenuChapter.interface";
import SectionMedia from '../SectionLayout/SectionMedia';
import { NIKE_EASE } from '../../../../rise/constants/nikerise';
import { DEFAULT_TRANSITION_DURATION } from '../ServiceMenuChapter.constants';
import { containsLanguage } from '../../../../../utilities';

const SECOND_SCENE_DELAY_S = 5;
const THIRD_SCENE_EXIT_DURATION_S = 2;

export function EntryMedia(props: { sections: ChapterSection[], scene: number }) {
    const {
        scene,
        sections = [],
    } = props;

    const rowsCount = useMemo(() => {
        return Math.ceil(sections.length / 2);
    }, [sections]);

    const expandedRowHeight = useMemo(() => {
        return `${100 / sections.length}vh`;
    }, [sections]);

    if (sections.length < 1) {
        return;
    }

    return (
        <motion.div
            style={{
                position: 'absolute',
            }}
            initial={{
                height: '50vh',
                top: '50vh',
                width: '100vw',
            }}
            animate={{
                top: 0,
                height: '100vh',
            }}
            transition={{
                delay: SECOND_SCENE_DELAY_S,
                duration: DEFAULT_TRANSITION_DURATION,
                ease: NIKE_EASE,
            }}
        >
            {sections.map((section, idx) => (
                <AnimatePresence>
                    {[1, 2].includes(scene) ? (
                        <motion.div
                            style={{
                                position: 'absolute',
                                display: 'flex',
                                width: '100vw',
                                flexDirection: idx % 2 ? 'row-reverse' : 'row',
                            }}
                            initial={{
                                top: `${sections.length % 2 ? Math.ceil(idx / 2) * 50 / rowsCount : Math.floor(idx / 2) * 50 / rowsCount}vh`,
                                height: `${50 / rowsCount}vh`,
                            }}
                            animate={{
                                height: expandedRowHeight,
                                top: `${100 / sections.length * idx}vh`,
                                transition: {
                                    delay: SECOND_SCENE_DELAY_S,
                                    duration: DEFAULT_TRANSITION_DURATION,
                                    ease: NIKE_EASE,
                                }
                            }}
                            exit={{
                                height: idx === 0 ? '100vh' : expandedRowHeight,
                                top: idx === 0 ? 0 : `${100 / sections.length * (idx - 1) + 100}vh`,
                                transition: {
                                    duration: THIRD_SCENE_EXIT_DURATION_S,
                                    ease: NIKE_EASE,
                                }
                            }}
                        >
                            <div style={{
                                width: '50%',
                                height: '100%',
                                zIndex: 10,
                                position: 'relative'
                            }}>
                                <SectionMedia media={section?.media} toApplyInternalEffect={false} />
                            </div>
                            <AnimatePresence>
                                <motion.p
                                    style={{
                                        width: '50%',
                                        padding: '2.08vh', // 40px
                                        fontSize: '3.91vh', // 75px
                                        fontFamily: 'HelveticaNowDisplay',
                                        fontWeight: '500',
                                        textTransform: 'uppercase',
                                        lineHeight: containsLanguage(section.expanded_title) === 'english' ? '3.2vh' : '1', // 61.50px
                                        whiteSpace: 'pre-line',
                                    }}
                                    initial={{
                                        opacity: idx === 0 ? 0 : 1,
                                    }}
                                    animate={{
                                        opacity: 1,
                                    }}
                                    transition={{
                                        delay: SECOND_SCENE_DELAY_S + 0.5,
                                        duration: DEFAULT_TRANSITION_DURATION,
                                        ease: NIKE_EASE,
                                    }}
                                >
                                    {section.expanded_title}
                                </motion.p>
                            </AnimatePresence>
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            ))}
            <AnimatePresence>
                {scene === 2 ? (
                    <motion.div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                        }}
                        initial={{
                            height: expandedRowHeight,
                            width: '50%',
                            zIndex: 0,
                        }}
                        exit={{
                            height: `100vh`,
                            width: '100vw',
                            zIndex: 10,
                        }}
                        transition={{
                            duration: THIRD_SCENE_EXIT_DURATION_S,
                            ease: NIKE_EASE,
                        }}
                    >
                        <SectionMedia media={sections[0]?.media} toApplyInternalEffect={false} />
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </motion.div>
    );
}
